/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export interface SizeGuideColumn {
    id?: string;
    sizeGuideId?: string;
    key: string;
    label: string;
    unit?: string;
    displayOrder?: number;
    /** Per-column how-to-measure diagram image (set in category template in Business Manager). */
    measurementImage?: string;
    /** Per-column instruction text (set in category template in Business Manager). */
    measurementLabel?: string;
}

export interface SizeGuideRow {
    id?: string;
    sizeGuideId?: string;
    sortOrder?: number;
    sizeValue: string; // e.g. "XS", "S", "6", "40"
    values: Record<string, string | number | undefined | null>;
}

export interface SizeGuide {
    id?: string;
    name?: string;
    categoryId?: string;
    productType?: string;
    unitSystem?: string;
    /** General fallback image for the How-to-Measure tab (shown if no per-column images exist). */
    generalMeasurementImage?: string;
    /** Kept for backward compatibility — use generalMeasurementImage for new data. */
    measurementImage?: string;
    measurementInstructions?: string;
    active?: boolean;
    version?: number | string;
    createdAt?: string;
    showUnavailableSizes?: boolean;
    columns: SizeGuideColumn[];
    rows: SizeGuideRow[];
}

/**
 * Salesforce Commerce Cloud Custom Attribute mappings
 */
export interface ProductCustomAttributes {
    c_sizeGuideId?: string;
    c_sizeGuideType?: string;
    c_sizeGuideOverride?: boolean;
    c_sizeGuideJson?: string | SizeGuide;
    c_measurementGuideImage?: string;
    c_measurementInstructions?: string;
}

/**
 * Filter columns dynamically:
 * If a product/brand does not provide data for a column across all rows,
 * that column is completely removed from being viewable.
 */
export function pruneEmptyColumns(columns: SizeGuideColumn[] = [], rows: SizeGuideRow[] = []): SizeGuideColumn[] {
    return columns.filter((col) => {
        return rows.some((row) => {
            const val = row.values?.[col.key];
            return val !== undefined && val !== null && String(val).trim() !== '' && String(val).trim() !== '-';
        });
    });
}

export function pruneEmptyRows(rows: SizeGuideRow[] = [], columns: SizeGuideColumn[] = []): SizeGuideRow[] {
    if (columns.length === 0) {
        return rows.filter((row) => Boolean(row.sizeValue && String(row.sizeValue).trim() !== ''));
    }
    return rows.filter((row) => {
        return columns.some((col) => {
            const val = row.values?.[col.key];
            return val !== undefined && val !== null && String(val).trim() !== '' && String(val).trim() !== '-';
        });
    });
}

/**
 * Parses and resolves dynamic SizeGuide strictly from the product object.
 * NO static fallback registries or hardcoded values.
 *
 * Used by SizeChartContent when an explicitGuide is already passed from the route loader.
 * For the primary resolution path (category template + product sizes), use
 * resolveSizeGuide() from '@/lib/size-guide/resolve.server'.
 */
export function parseProductSizeGuide(
    product?: Record<string, any> | null,
    explicitGuide?: SizeGuide
): SizeGuide | null {
    if (explicitGuide) {
        const activeColumns = pruneEmptyColumns(explicitGuide.columns, explicitGuide.rows);
        const activeRows =
            explicitGuide.showUnavailableSizes !== false
                ? explicitGuide.rows
                : pruneEmptyRows(explicitGuide.rows, activeColumns);
        return {
            ...explicitGuide,
            columns: activeColumns,
            rows: activeRows,
        };
    }

    if (!product) return null;

    // --- AREA 1: Check SCAPI Variation Attributes & Variants ---
    const sizeAttr = product.variationAttributes?.find((attr: any) => attr.id?.toLowerCase() === 'size');
    const scapiSizes: string[] = sizeAttr?.values?.map((v: any) => v.value || v.name) || [];

    // Also check product.c_size or variants if variationAttributes is not present
    if (scapiSizes.length === 0 && product.c_size) {
        scapiSizes.push(String(product.c_size));
    }
    if (scapiSizes.length === 0 && Array.isArray(product.variants)) {
        product.variants.forEach((variant: any) => {
            const val = variant.variationValues?.size || variant.variationValues?.Size;
            if (val && !scapiSizes.includes(val)) {
                scapiSizes.push(val);
            }
        });
    }

    // --- AREA 2: Check SFCC Custom Size Guide Attributes ---
    const rawGuide = product.c_sizeGuideJson || product.c_sizeGuide;
    let parsed: SizeGuide | null = null;
    if (rawGuide) {
        if (typeof rawGuide === 'string') {
            try {
                parsed = JSON.parse(rawGuide);
            } catch (err) {
                console.warn('Failed to parse product size guide JSON:', err);
            }
        } else if (typeof rawGuide === 'object') {
            parsed = rawGuide as SizeGuide;
        }
    }

    // If custom guide JSON is provided, use its rows directly.
    // SCAPI variation attributes determine orderable / available status in the UI,
    // not whether the row appears in the chart!
    if (parsed && parsed.rows?.length > 0) {
        // Keep all template rows intact so XS to max appear.
        if (parsed.showUnavailableSizes === false && scapiSizes.length > 0) {
            const scapiSet = new Set(scapiSizes.map((s) => s.toUpperCase()));
            const matched = parsed.rows.filter((r) => scapiSet.has(r.sizeValue.toUpperCase()));
            if (matched.length > 0) {
                parsed.rows = matched;
            }
        }
    } else if (!parsed && scapiSizes.length > 0) {
        // If no custom guide JSON, generate basic Size Guide directly from SCAPI sizes
        parsed = {
            id: product.id || 'product-sizes',
            name: product.name || 'Size Chart',
            columns: [],
            rows: scapiSizes.map((size) => ({
                sizeValue: size,
                values: {},
            })),
        };
    }

    if (!parsed) return null;

    // Apply product-specific measurement image / instruction overrides if present
    if (product.c_measurementGuideImage) {
        parsed.generalMeasurementImage = product.c_measurementGuideImage;
        parsed.measurementImage = product.c_measurementGuideImage;
    }
    if (product.c_measurementInstructions) {
        parsed.measurementInstructions = product.c_measurementInstructions;
    }

    const activeColumns = pruneEmptyColumns(parsed.columns || [], parsed.rows || []);
    const activeRows = pruneEmptyRows(parsed.rows || [], activeColumns);
    return {
        ...parsed,
        columns: activeColumns,
        rows: activeRows,
    };
}
